<template>
  <div class="modal-overlay">
    <div class="modal-content">
      <!-- Always Visible Header -->
      <h2 class="modal-title">Confirm Reservation</h2>
      <div class="summary-details">
        <h3>{{ tourName }}</h3>
        <p class="time">{{ duration }} · Remote Drone Tour</p>
      </div>

      <!-- Two-Step Accordion for Guest Info Flow -->
      <div v-if="showGuestInfoForm" class="accordion-container">
        <!-- Step 1: Guest Information -->
        <div class="accordion-item">
          <div
            class="accordion-header"
            :class="{ active: currentStep === 1, completed: currentStep > 1 }"
            @click="currentStep === 2 ? (currentStep = 1) : null"
          >
            <vue-feather
              :type="currentStep > 1 ? 'check-circle' : 'users'"
              size="18"
            ></vue-feather>
            <span>Guest Information</span>
            <vue-feather
              :type="currentStep === 1 ? 'chevron-down' : 'chevron-right'"
              size="16"
              class="chevron"
            ></vue-feather>
          </div>

          <div v-if="currentStep === 1" class="accordion-content">
            <p class="guest-info-description">
              Please provide details for each guest. They will receive flight
              notifications via email.
            </p>

            <div class="guest-form-container">
              <div
                v-for="(guest, index) in guestInfo"
                :key="index"
                class="guest-inputs"
              >
                <input
                  :id="`guest-name-${index}`"
                  type="text"
                  :value="guest.name"
                  @input="updateGuestInfo(index, 'name', $event.target.value)"
                  :placeholder="`Guest ${index + 1} Name`"
                  class="guest-input"
                  :class="{ error: guest.name && !guest.name.trim() }"
                />
                <input
                  :id="`guest-email-${index}`"
                  type="email"
                  :value="guest.email"
                  @input="updateGuestInfo(index, 'email', $event.target.value)"
                  :placeholder="`Guest ${index + 1} Email`"
                  class="guest-input"
                  :class="{
                    error: guest.email && !isValidEmail(guest.email),
                  }"
                />
              </div>
            </div>

            <div class="step-actions">
              <button class="cancel-btn" @click="$emit('close')">Cancel</button>
              <button
                class="next-btn"
                :disabled="!isGuestInfoValid"
                @click="currentStep = 2"
              >
                Continue to Confirmation
              </button>
            </div>
          </div>
        </div>

        <!-- Step 2: Confirmation -->
        <div class="accordion-item">
          <div class="accordion-header" :class="{ active: currentStep === 2 }">
            <vue-feather type="check-square" size="18"></vue-feather>
            <span>Confirm Reservation</span>
            <vue-feather
              :type="currentStep === 2 ? 'chevron-down' : 'chevron-right'"
              size="16"
              class="chevron"
            ></vue-feather>
          </div>

          <div v-if="currentStep === 2" class="accordion-content">
            <Divider />
            <div class="details-grid">
              <div class="detail-item">
                <h4>Date</h4>
                <p>{{ formatDate(selectedDate) }}</p>
              </div>
              <div class="detail-item">
                <h4>Event Time</h4>
                <p>{{ selectedSlot }}</p>
              </div>
              <div class="detail-item">
                <h4>Location</h4>
                <p>{{ location }}</p>
              </div>
              <div class="detail-item">
                <h4>Tickets</h4>
                <p>{{ guestCount }} ticket{{ guestCount > 1 ? "s" : "" }}</p>
              </div>
            </div>

            <div class="price-summary">
              <div class="price-row">
                <span>Credits Required</span>
                <span
                  >{{ totalCreditsNeeded }} credit{{
                    totalCreditsNeeded > 1 ? "s" : ""
                  }}</span
                >
              </div>
              <!-- <div class="price-row">
                <span>Total Cost</span>
                <span>${{ totalCost }}</span>
              </div> -->
              <div class="price-row total">
                <span>Your Credit Balance</span>
                <span>{{ creditBalance }} credits</span>
              </div>
              <div v-if="hasInsufficientCredits" class="recharge-notice">
                <p>You need credits to book this tour.</p>
                <button class="recharge-btn" @click="goToCreditsPage">
                  <vue-feather type="plus-circle" size="16"></vue-feather>
                  Recharge Credits
                </button>
              </div>
            </div>

            <div class="step-actions">
              <button class="back-btn" @click="currentStep = 1">Back</button>
              <button
                class="confirm-btn"
                :disabled="hasInsufficientCredits || isLoading"
                @click="handleConfirm"
              >
                <div class="button-content">
                  <div v-if="isLoading" class="spinner"></div>
                  <span>{{
                    isLoading
                      ? "Processing..."
                      : hasInsufficientCredits
                      ? "Insufficient Credits"
                      : "Confirm Booking"
                  }}</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Original Single-Step Flow (for non-agent or single guest) -->
      <div v-else class="reservation-summary">
        <div class="summary-details">
          <br />
          <Divider />
          <div class="details-grid">
            <div class="detail-item">
              <h4>Date</h4>
              <p>{{ formatDate(selectedDate) }}</p>
            </div>
            <div class="detail-item">
              <h4>Event Time</h4>
              <p>{{ selectedSlot }}</p>
            </div>
            <div class="detail-item">
              <h4>Location</h4>
              <p>{{ location }}</p>
            </div>
            <div class="detail-item">
              <h4>Tickets</h4>
              <p>{{ guestCount }} ticket{{ guestCount > 1 ? "s" : "" }}</p>
            </div>
          </div>

          <div class="price-summary">
            <div class="price-row">
              <span>Credits Required</span>
              <span
                >{{ totalCreditsNeeded }} credit{{
                  totalCreditsNeeded > 1 ? "s" : ""
                }}</span
              >
            </div>
            <!-- <div class="price-row">
              <span>Total Cost</span>
              <span>${{ totalCost }}</span>
            </div> -->
            <div class="price-row total">
              <span>Your Credit Balance</span>
              <span>{{ creditBalance }} credits</span>
            </div>
            <div v-if="hasInsufficientCredits" class="recharge-notice">
              <p>You need credits to book this tour.</p>
              <button class="recharge-btn" @click="goToCreditsPage">
                <vue-feather type="plus-circle" size="16"></vue-feather>
                Recharge Credits
              </button>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button
            class="cancel-btn"
            @click="$emit('close')"
            :disabled="isLoading"
          >
            Cancel
          </button>
          <button
            class="confirm-btn"
            :disabled="hasInsufficientCredits || isLoading"
            @click="handleConfirm"
          >
            <div class="button-content">
              <div v-if="isLoading" class="spinner"></div>
              <span>{{
                isLoading
                  ? "Processing..."
                  : hasInsufficientCredits
                  ? "Insufficient Credits"
                  : "Confirm Booking"
              }}</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Divider from "@/components/organisms/Divider.vue";
import VueFeather from "vue-feather";

export default {
  props: {
    tourName: {
      type: String,
      required: true,
    },
    tourImage: {
      type: String,
      required: true,
    },
    location: {
      type: String,
      required: true,
    },
    duration: {
      type: String,
      required: true,
    },
    selectedDate: {
      type: Object,
      required: true,
    },
    selectedSlot: {
      type: String,
      required: true,
    },
    creditBalance: {
      type: Number,
      required: true,
    },
    guestCount: {
      type: Number,
      required: false,
      default: 1,
    },
    isAgent: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    Divider,
    VueFeather,
  },
  data() {
    return {
      isLoading: false,
      guestInfo: [],
      currentStep: 1, // 1 = Guest Info, 2 = Confirmation
    };
  },
  computed: {
    totalCreditsNeeded() {
      return this.guestCount;
    },
    hasInsufficientCredits() {
      return this.creditBalance < this.totalCreditsNeeded;
    },
    showGuestInfoForm() {
      return this.isAgent;
    },
    isGuestInfoValid() {
      if (!this.showGuestInfoForm) return true;
      return (
        this.guestInfo.length === this.guestCount &&
        this.guestInfo.every(
          (guest) => guest.name && guest.email && this.isValidEmail(guest.email)
        )
      );
    },
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    initializeGuestInfo() {
      this.guestInfo = Array.from({ length: this.guestCount }, () => ({
        name: "",
        email: "",
      }));
    },
    updateGuestInfo(index, field, value) {
      if (!this.guestInfo[index]) {
        this.guestInfo[index] = { name: "", email: "" };
      }
      this.guestInfo[index][field] = value;
    },
    handleConfirm() {
      if (this.showGuestInfoForm && !this.isGuestInfoValid) {
        return; // Don't proceed if guest info is invalid
      }

      this.isLoading = true;

      setTimeout(() => {
        this.$emit("confirm", {
          guestInfo: this.guestInfo,
        });
      }, 2000);
    },
    goToCreditsPage() {
      this.$emit("close");
      this.$router.push("/credits");
    },
  },
  watch: {
    guestCount: {
      handler() {
        if (this.showGuestInfoForm) {
          this.initializeGuestInfo();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
}

.tour-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.summary-details h3 {
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.location,
.time {
  color: #666;
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
}

.detail-item h4 {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.detail-item p {
  font-size: 16px;
  color: #111827;
  font-weight: 500;
}

.price-summary {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.price-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.price-row.total {
  font-weight: bold;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  /* border-top: 1px solid #eee; */
}

.modal-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.cancel-btn,
.confirm-btn {
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cancel-btn {
  background: #f5f5f5;
  color: #333;
}

.confirm-btn {
  background: #000;
  color: white;
}

.confirm-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  vertical-align: middle;
  margin-right: 8px;
}

.confirm-btn span,
.confirm-btn template {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.recharge-notice {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #eee;
}

.recharge-notice p {
  margin: 0 0 0.75rem 0;
  font-size: 14px;
  color: #374151;
  text-align: center;
}

.recharge-btn {
  width: 100%;
  padding: 0.6rem;
  background-color: #fee2e2 !important;
  color: #dc2626 !important;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background-color 0.2s;
}

.recharge-btn:hover {
  background-color: #333;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Guest Information Form Styles */
.guest-info-section {
  margin-top: 1.5rem;
}

.guest-info-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.guest-info-description {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
}

.guest-form-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.guest-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.guest-input {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  background-color: white;
}

.guest-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.guest-input.error {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

/* Accordion Styles */
.accordion-container {
  margin-top: 1.5rem;
}

.accordion-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  overflow: hidden;
}

.accordion-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background-color: #f9fafb;
  cursor: pointer;
  border-bottom: 1px solid transparent;
  transition: all 0.2s ease;
}

.accordion-header:hover {
  background-color: #f3f4f6;
}

.accordion-header.active {
  background-color: #eff6ff;
  border-bottom-color: #e5e7eb;
}

.accordion-header.completed {
  background-color: #f0fdf4;
  color: #059669;
}

.accordion-header span {
  flex: 1;
  font-weight: 600;
  font-size: 0.95rem;
}

.accordion-header .chevron {
  transition: transform 0.2s ease;
}

.accordion-content {
  padding: 1.5rem 1.25rem;
  background-color: white;
}

.step-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.step-actions .cancel-btn,
.step-actions .back-btn {
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-actions .next-btn,
.step-actions .confirm-btn {
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: #000;
  color: white;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-actions .next-btn:disabled,
.step-actions .confirm-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.step-actions .cancel-btn:hover,
.step-actions .back-btn:hover {
  background-color: #f9fafb;
}

/* Responsive adjustments for guest form */
@media (max-width: 640px) {
  .guest-inputs {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .accordion-header {
    padding: 0.875rem 1rem;
  }

  .accordion-content {
    padding: 1.25rem 1rem;
  }

  .step-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
}
</style>
